import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Badge, Button, Card, Checkbox, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle, Switch, Text, makeStyles, tokens } from "@fluentui/react-components";
import { WeekManager } from "./Schedule.tsx";
import { ConfirmButton } from "../components/ConfirmButton.tsx";
import { LockClosed16Regular } from "@fluentui/react-icons";
import { addDays, format, startOfWeek } from "date-fns";
import { CELLS_PER_DAY, CELL_MIN, DAYS, GRID_START_MIN, cellsChanged, cellsToSlots, emptyCells, slotsToCells, toMin, type Cells, type Weekday } from "@gymplan/shared";
import { api, type AvailabilitySlot, type Client } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { WEEKDAY_LABEL } from "../lib/format.ts";
import { AvailabilityGrid, type CellMark } from "../components/AvailabilityGrid.tsx";
import { AvailabilityChat, type Proposal } from "../components/AvailabilityChat.tsx";
import type { Knowledge } from "@gymplan/shared";
import { InviteSend } from "../components/InviteSend.tsx";
import { ClientProgress } from "../components/ClientProgress.tsx";

const useStyles = makeStyles({
  weeks: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", alignItems: "start", "@media (max-width: 1100px)": { gridTemplateColumns: "minmax(0, 1fr)" } },
  card: { padding: "14px", display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 },
  head: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  top: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "12px 24px", flexWrap: "wrap", marginBottom: "12px" },
  saveRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
});

type Which = "this" | "next";
type WeekData = { source: "template" | "week"; slots: AvailabilitySlot[] };
type Status = "PLANNING" | "HOLIDAY" | "COLLECTING" | "PROPOSED" | "PUBLISHED";
interface ScheduleData {
  timezone: string;
  schedule: { status: Status; checkInsSentAt: string | null; deadlineAt: string | null; holidayNotifiedAt: string | null; unplaced: unknown[] | null; notes: unknown[] | null };
  fallbackPublishLabel: string;
  checkInRunLabel: string;
  lock: { locked: boolean; started: boolean; reason: string | null };
  sessions: { startsAt: string; endsAt: string; status: string; client: { id: string; name: string } }[];
  checkIns: { status: string; client: { id: string; name: string } }[];
}
type Booking = { weekday: Weekday; startTime: string; endTime: string; label: string; proposed?: boolean };

/** Session times in the trainer's timezone, as grid bookings. */
function toBookings(d: ScheduleData | undefined): Booking[] {
  if (!d) return [];
  const f = new Intl.DateTimeFormat("en-GB", { timeZone: d.timezone, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const parts = (iso: string) => {
    const p = Object.fromEntries(f.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
    return { weekday: (p.weekday as string).slice(0, 3).toUpperCase() as Weekday, time: `${p.hour}:${p.minute}` };
  };
  return d.sessions.map((x) => {
    const a = parts(x.startsAt);
    return { weekday: a.weekday, startTime: a.time, endTime: parts(x.endsAt).time, label: x.client.name.split(" ")[0] ?? x.client.name, proposed: x.status === "PROPOSED" };
  });
}
const fmtWhen = (iso: string | null, tz: string) => (iso ? new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(iso)) : "");

/**
 * The trainer's one page for planning: this week and next side by side — hours, bookings, where each week
 * is in the weekly cycle and what to do next — with a chat above for changes and each week's sessions &
 * replies underneath. Weeks after next follow the usual week.
 */
export function AvailabilityPage() {
  const [params] = useSearchParams();
  const s = useStyles();
  const qc = useQueryClient();
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weeks: Record<Which, Date> = { this: monday, next: addDays(monday, 7) };
  const ymd: Record<Which, string> = { this: format(weeks.this, "yyyy-MM-dd"), next: format(weeks.next, "yyyy-MM-dd") };
  const clients = useQuery({ queryKey: ["clients"], queryFn: () => api.get<Client[]>("/api/clients") });
  const [showPrefs, setShowPrefs] = useState(false);
  const [preview, setPreview] = useState<Proposal | null>(null);
  const thisQ = useQuery({ queryKey: ["availability", ymd.this], queryFn: () => api.get<WeekData>(`/api/trainers/me/availability?week=${ymd.this}`) });
  const nextQ = useQuery({ queryKey: ["availability", ymd.next], queryFn: () => api.get<WeekData>(`/api/trainers/me/availability?week=${ymd.next}`) });
  const thisS = useQuery({ queryKey: ["schedule", ymd.this], queryFn: () => api.get<ScheduleData>(`/api/schedule?week=${ymd.this}`) });
  const nextS = useQuery({ queryKey: ["schedule", ymd.next], queryFn: () => api.get<ScheduleData>(`/api/schedule?week=${ymd.next}`) });
  const nextLocked = nextS.data?.schedule.status === "COLLECTING";
  const { user } = useAuth();
  const t = user?.trainer;
  const inviteUrl = t ? `${window.location.origin}/join/${t.inviteToken}` : "";
  const justSetUp = !!(useLocation().state as { justSetUp?: boolean } | null)?.justSetUp;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["availability"] });
    void qc.invalidateQueries({ queryKey: ["schedule"] });
  };

  /** Save from the chat. "usual" updates the template; weeks still on the template follow it. */
  const saveProposal = useCallback(
    async (p: Proposal) => {
      // A booked week only changes with the trainer's confirmation (they've seen who's affected).
      const body = { slots: p.slots.map((x) => ({ ...x, gymId: null })), via: "text", confirmImpact: !!p.booked };
      if (p.target === "usual") await api.put("/api/trainers/me/availability", body);
      else await api.put(`/api/trainers/me/availability?week=${ymd[p.target]}`, body);
      refresh();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ymd.this, ymd.next],
  );

  // Which grids show a chat preview: the targeted week, or (for "usual") any week still following it.
  const previewFor = (w: Which, data: WeekData | undefined) => {
    if (!preview) return null;
    if (preview.target === w) return preview.slots;
    if (preview.target === "usual" && data?.source === "template") return preview.slots;
    return null;
  };

  // What the chat can tell the trainer about their clients: the data this page already shows.
  const knowledge: Knowledge = useMemo(
    () => ({ clients: clients.data ?? [], weeks: [{ label: "this week", monday: weeks.this, data: thisS.data }, { label: "next week", monday: weeks.next, data: nextS.data }] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients.data, thisS.data, nextS.data, ymd.this],
  );

  const prefMarks = useMemo(() => {
    if (!showPrefs) return undefined;
    const m: Partial<Record<Weekday, (CellMark | null)[]>> = {};
    for (const c of clients.data ?? []) {
      for (const p of c.preferences) {
        const d = p.weekday as Weekday;
        const a = p.startTime ? toMin(p.startTime) : GRID_START_MIN;
        const b = p.endTime ? toMin(p.endTime) : GRID_START_MIN + CELLS_PER_DAY * CELL_MIN;
        m[d] ??= Array<CellMark | null>(CELLS_PER_DAY).fill(null);
        for (let i = 0; i < CELLS_PER_DAY; i++) {
          const cs = GRID_START_MIN + i * CELL_MIN;
          if (cs < b && cs + CELL_MIN > a) m[d]![i] = "pref";
        }
      }
    }
    return m;
  }, [showPrefs, clients.data]);

  return (
    <div>
      <div className={s.top}>
        <div style={{ minWidth: 0 }}>
          <Text as="h1" size={800} weight="semibold" block>
            Hi {user?.name.split(" ")[0]}
          </Text>
          <Text block style={{ color: tokens.colorNeutralForeground3 }}>
            {t?.gyms.find((g) => g.isPrimary)?.name ?? "No gym set"} · {t?.sessionLengthMinutes} min sessions · check-ins every {t ? WEEKDAY_LABEL[t.availabilityRequestDay] : "—"}
          </Text>
        </div>
        {inviteUrl && <InviteSend url={inviteUrl} trainerFirst={user?.name.split(" ")[0] ?? ""} />}
      </div>

      <ClientProgress trainerFirst={user?.name.split(" ")[0] ?? ""} justSetUp={justSetUp} />

      <AvailabilityChat weeks={ymd} nextLocked={nextLocked} knowledge={knowledge} onPreview={setPreview} onSave={saveProposal} />

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Switch label="Show client preferences" checked={showPrefs} onChange={(_, d) => setShowPrefs(d.checked)} />
      </div>
      <div className={s.weeks}>
        <WeekCard title="This week" which="this" monday={weeks.this} ymd={ymd.this} q={thisQ.data} sched={thisS.data} preview={previewFor("this", thisQ.data)} marks={prefMarks} onSaved={refresh} noHint />
        <WeekCard title="Next week" which="next" monday={weeks.next} ymd={ymd.next} q={nextQ.data} sched={nextS.data} preview={previewFor("next", nextQ.data)} marks={prefMarks} onSaved={refresh} />
      </div>
      <Text size={200} block style={{ color: tokens.colorNeutralForeground3, marginTop: 10 }}>
        Weeks after next follow your usual week. Tick “Make this my usual week” when saving to change it.
      </Text>

      <WeekDetails ymd={ymd} sched={{ this: thisS.data, next: nextS.data }} focus={params.get("week")} />
    </div>
  );
}

function WeekCard({ title, which, monday, ymd, q, sched, preview, marks, onSaved, noHint }: { title: string; which: Which; monday: Date; ymd: string; q: WeekData | undefined; sched: ScheduleData | undefined; preview: AvailabilitySlot[] | { weekday: string; startTime: string; endTime: string }[] | null; marks?: Partial<Record<Weekday, (CellMark | null)[]>>; onSaved: () => void; noHint?: boolean }) {
  const s = useStyles();
  const status = sched?.schedule.status ?? "PLANNING";
  // This week is under way; next week locks while clients are being asked (and once built).
  const locked = which === "this" || !!sched?.lock.locked;
  const bookings = which === "this" || status === "PROPOSED" || status === "PUBLISHED" ? toBookings(sched) : [];
  const replied = sched?.checkIns.filter((c) => c.status === "COMPLETED" || c.status === "EXPIRED").length ?? 0;
  const act = useMutation({ mutationFn: (v: { path: string; body?: object }) => api.post(v.path, { week: ymd, ...(v.body ?? {}) }), onSuccess: onSaved });
  const go = (path: string, body?: object) => act.mutate({ path, body });
  const saved = useMemo(() => (q ? slotsToCells(q.slots) : null), [q]);
  const [cells, setCells] = useState<Cells>(emptyCells);
  const [makeUsual, setMakeUsual] = useState(false);
  useEffect(() => {
    if (saved) setCells(saved);
  }, [saved]);
  const dirty = !!saved && cellsChanged(saved, cells) > 0;
  const url = `/api/trainers/me/availability?week=${ymd}`;

  const save = useMutation({
    mutationFn: async () => {
      const body = { slots: cellsToSlots(cells).map((x) => ({ ...x, gymId: null })) };
      if (makeUsual) {
        await api.put("/api/trainers/me/availability", body); // the usual week…
        if (q?.source === "week") await api.del(url); // …and this week follows it again
      } else await api.put(url, body);
    },
    onSuccess: () => {
      setMakeUsual(false);
      onSaved();
    },
  });
  const reset = useMutation({ mutationFn: () => api.del(url), onSuccess: onSaved });

  // Chat preview: show the proposed week with added/removed cells marked; editing pauses meanwhile.
  const previewCells = preview ? slotsToCells(preview) : null;
  const previewMarks = useMemo(() => {
    if (!previewCells || !saved) return undefined;
    const m: Partial<Record<Weekday, (CellMark | null)[]>> = {};
    for (const d of DAYS) {
      m[d] = Array.from({ length: CELLS_PER_DAY }, (_, i) => (previewCells[d][i] && !saved[d][i] ? "added" : !previewCells[d][i] && saved[d][i] ? "removed" : null));
    }
    return m;
  }, [previewCells, saved]);

  return (
    <Card className={s.card}>
      <div className={s.head}>
        <Text weight="semibold" size={500}>{title}</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          {format(monday, "d MMM")} – {format(addDays(monday, 6), "d MMM")}
        </Text>
        <div style={{ flex: 1 }} />
        {locked ? (
          <Badge appearance="tint" color="subtle" icon={<LockClosed16Regular />}>Locked</Badge>
        ) : (
          q && (q.source === "week" ? <Badge appearance="tint" color="brand">Changed for this week</Badge> : <Badge appearance="outline" color="informative">Your usual week</Badge>)
        )}
      </div>
      {which === "next" && sched && status === "PLANNING" && (
        <MessageBar intent="info" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>This will run on {sched.checkInRunLabel}</MessageBarTitle>
            That's when RackUp messages your clients to ask when they can train next week. Set these hours before then.
          </MessageBarBody>
          <MessageBarActions>
            <ConfirmButton size="small" title="Holiday week?" message="No sessions will be scheduled, and your clients will be told when check-ins would normally go out. Continue?" confirmLabel="Mark as holiday" onConfirm={() => go("/api/schedule/holiday", { holiday: true })}>Away that week?</ConfirmButton>
            <ConfirmButton size="small" appearance="primary" title="Ask clients now?" message="Every client gets the WhatsApp check-in for next week straight away, and next week's hours lock." confirmLabel="Ask now" onConfirm={() => go("/api/schedule/send-checkins")}>Ask clients now</ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}
      {status === "HOLIDAY" && (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Holiday week</MessageBarTitle>
            {sched?.schedule.holidayNotifiedAt ? `Your clients were told ${fmtWhen(sched.schedule.holidayNotifiedAt, sched.timezone)}.` : `On ${sched?.checkInRunLabel} your clients will be told there are no sessions.`}
          </MessageBarBody>
          {!sched?.schedule.holidayNotifiedAt && (
            <MessageBarActions>
              <Button size="small" onClick={() => go("/api/schedule/holiday", { holiday: false })}>Undo</Button>
              <ConfirmButton size="small" appearance="primary" title="Tell clients now?" message="Every client gets a WhatsApp saying there are no sessions that week." confirmLabel="Send" onConfirm={() => go("/api/schedule/holiday/notify")}>Tell clients now</ConfirmButton>
            </MessageBarActions>
          )}
        </MessageBar>
      )}
      {status === "COLLECTING" && sched && (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Out to your clients now</MessageBarTitle>
            RackUp asked your clients for their availability {fmtWhen(sched.schedule.checkInsSentAt, sched.timezone) ? `on ${fmtWhen(sched.schedule.checkInsSentAt, sched.timezone)}` : ""} and is waiting for replies — {replied} of {sched.checkIns.length} back
            {sched.schedule.deadlineAt ? `, closes ${fmtWhen(sched.schedule.deadlineAt, sched.timezone)}` : ""}. The hours are locked until the schedule is built.
          </MessageBarBody>
          <MessageBarActions>
            <ConfirmButton size="small" title="Build now?" message="Builds the schedule from the replies so far. Anyone who hasn't replied gets their usual times." confirmLabel="Build" onConfirm={() => go("/api/schedule/build")}>Build now</ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}
      {status === "PROPOSED" && sched && (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Ready for you to check</MessageBarTitle>
            Proposed sessions are shown dashed. Adjust them under “sessions & replies” below, then publish — clients get their times on WhatsApp. If you do nothing it publishes itself {sched.fallbackPublishLabel}.
          </MessageBarBody>
          <MessageBarActions>
            <ConfirmButton size="small" title="Rebuild?" message="Runs the placement again from scratch. Manual changes to this week are lost." confirmLabel="Rebuild" onConfirm={() => go("/api/schedule/build")}>Rebuild</ConfirmButton>
            <ConfirmButton size="small" appearance="primary" title="Publish?" message={`Confirms ${sched.sessions.length} sessions and WhatsApps every client their times.`} confirmLabel="Publish" onConfirm={() => go("/api/schedule/publish")}>Publish</ConfirmButton>
          </MessageBarActions>
        </MessageBar>
      )}
      {which === "next" && status === "PUBLISHED" && (
        <MessageBar intent="success">
          <MessageBarBody>Next week is published — bookings shown in green.</MessageBarBody>
        </MessageBar>
      )}
      {act.isError && <MessageBar intent="error"><MessageBarBody>{(act.error as Error).message}</MessageBarBody></MessageBar>}
      {previewCells ? (
        <AvailabilityGrid weekStart={monday} value={previewCells} readOnly marks={previewMarks} bookings={bookings} noQuickStart bare />
      ) : locked ? (
        <AvailabilityGrid weekStart={monday} value={saved ?? emptyCells()} readOnly tone="locked" bookings={bookings} noQuickStart bare />
      ) : (
        <AvailabilityGrid
          weekStart={monday}
          value={cells}
          onChange={setCells}
          marks={marks}
          noHint={noHint}
          bare
          actions={
            <div className={s.saveRow}>
              {q?.source === "week" && !dirty && (
                <Button size="small" appearance="subtle" disabled={reset.isPending} onClick={() => confirm(`Go back to your usual hours for ${title.toLowerCase()}?`) && reset.mutate()}>
                  Use usual hours
                </Button>
              )}
              {dirty && (
                <>
                  <Checkbox label="Make this my usual week" checked={makeUsual} onChange={(_, d) => setMakeUsual(!!d.checked)} />
                  <Button size="small" appearance="subtle" onClick={() => saved && setCells(saved)}>Undo</Button>
                </>
              )}
              <Button size="small" appearance="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          }
        />
      )}
      {save.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{(save.error as Error).message}</MessageBarBody>
        </MessageBar>
      )}
    </Card>
  );
}

/** Each week's sessions & replies (the session editor, who couldn't be fitted, client notes, check-ins). */
function WeekDetails({ ymd, sched, focus }: { ymd: Record<Which, string>; sched: Record<Which, ScheduleData | undefined>; focus: string | null }) {
  // Open a week when a link points at it (WhatsApp/email links use ?week=) or when it needs attention.
  const needs = (w: Which) => {
    const d = sched[w];
    return !!d && d.schedule.status === "PROPOSED" && ((d.schedule.unplaced?.length ?? 0) > 0 || (d.schedule.notes?.length ?? 0) > 0);
  };
  const initial = (["this", "next"] as Which[]).filter((w) => focus === ymd[w] || needs(w));
  const [open, setOpen] = useState<string[] | null>(null);
  const openItems = open ?? initial;
  const summary = (w: Which) => {
    const d = sched[w];
    if (!d) return "";
    const replied = d.checkIns.filter((c) => c.status === "COMPLETED").length;
    return `${d.sessions.length} session${d.sessions.length === 1 ? "" : "s"}${d.checkIns.length ? ` · ${replied} of ${d.checkIns.length} replies` : ""}${needs(w) ? " · needs you" : ""}`;
  };
  return (
    <Accordion multiple collapsible openItems={openItems} onToggle={(_, d) => setOpen(d.openItems as string[])} style={{ marginTop: 16 }}>
      {(["this", "next"] as Which[]).map((w) => (
        <AccordionItem key={w} value={w}>
          <AccordionHeader size="large">
            <span>
              <Text weight="semibold">{w === "this" ? "This week" : "Next week"} — sessions & replies</Text>
              <Text size={200} style={{ marginLeft: 8, color: tokens.colorNeutralForeground3 }}>{summary(w)}</Text>
            </span>
          </AccordionHeader>
          <AccordionPanel>{openItems.includes(w) && <WeekManager weekYmd={ymd[w]} />}</AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
